import React, { useEffect, useState } from "react";
import styled from "styled-components";
import api from "../api";

const Page = styled.div`
  background: #f8fafc;
  min-height: 100vh;
  padding: 16px;
  color: #0f172a;
`;
const Head = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  flex-wrap: wrap;
`;
const Title = styled.h2`
  margin: 0;
  font-size: 1.6rem;
  color: #0f172a;
  font-weight: 800;
`;
const Card = styled.div`
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  box-shadow: 0 10px 22px rgba(15, 23, 42, 0.08);
`;
const Btn = styled.button`
  min-height: 44px;
  border: none;
  border-radius: 10px;
  padding: 0 14px;
  font-weight: 700;
  cursor: pointer;
  background: ${(p) => (p.$secondary ? "#e2e8f0" : "#2563eb")};
  color: ${(p) => (p.$secondary ? "#0f172a" : "#fff")};
`;
const Input = styled.input`
  width: 100%;
  min-height: 44px;
  border: 1px solid #cbd5e1;
  border-radius: 10px;
  padding: 10px 12px;
  color: #0f172a;
  background: #fff;
`;
const TableWrap = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
`;
const Table = styled.table`
  width: 100%;
  min-width: 900px;
  border-collapse: collapse;
  th,
  td {
    border-bottom: 1px solid #e2e8f0;
    padding: 12px 10px;
    text-align: left;
    color: #0f172a;
    vertical-align: middle;
  }
  th {
    color: #64748b;
    font-size: 0.76rem;
    text-transform: uppercase;
  }
`;
const Badge = styled.span`
  font-size: 0.75rem;
  font-weight: 700;
  border-radius: 999px;
  padding: 4px 10px;
  display: inline-block;
  background: ${(p) => (p.$active ? "#dcfce7" : "#e5e7eb")};
  color: ${(p) => (p.$active ? "#166534" : "#374151")};
`;
const ActionLink = styled.button`
  border: none;
  background: none;
  color: ${(p) => p.$color || "#2563eb"};
  font-weight: 700;
  cursor: pointer;
`;
const Toolbar = styled.div`
  padding: 12px;
`;
const ModalBg = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(2, 6, 23, 0.45);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1200;
`;
const Modal = styled.div`
  width: 100%;
  max-width: 760px;
  background: #fff;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 22px 46px rgba(2, 6, 23, 0.25);
  max-height: 92vh;
  overflow-y: auto;
  @media (max-width: 768px) {
    max-width: 100%;
    height: 100vh;
    border-radius: 0;
  }
`;
const ModalHead = styled.div`
  padding: 14px 16px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;
const Body = styled.div`
  padding: 16px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;
const Label = styled.label`
  display: block;
  margin-bottom: 6px;
  font-size: 0.8rem;
  color: #334155;
  font-weight: 700;
`;
const Field = styled.div``;
const Select = styled.select`
  width: 100%;
  min-height: 44px;
  border: 1px solid #cbd5e1;
  border-radius: 10px;
  padding: 10px 12px;
  color: #0f172a;
  background: #fff;
`;
const Skeleton = styled.div`
  height: ${(p) => p.$h || 44}px;
  border-radius: 8px;
  background: linear-gradient(90deg, #e2e8f0 25%, #f8fafc 50%, #e2e8f0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.3s infinite;
  @keyframes shimmer {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;
const ErrorBox = styled.div`
  margin-bottom: 10px;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid #fecaca;
  background: #fef2f2;
  color: #b91c1c;
  font-weight: 700;
  font-size: 0.86rem;
`;

function BranchManagementScreen() {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [branches, setBranches] = useState([]);
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState({
    name: "",
    city: "",
    address: "",
    manager_id: "",
  });

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const [branchRes, userRes] = await Promise.all([
        api.get("/admin/branches"),
        api.get("/admin/users", { params: { page: 1, limit: 100 } }),
      ]);
      setBranches(branchRes?.data?.data || []);
      setUsers(userRes?.data?.data || []);
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to load branches.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = branches.filter((b) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (b.name || "").toLowerCase().includes(q) || (b.city || "").toLowerCase().includes(q);
  });

  const createBranch = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      await api.post("/admin/branches", {
        name: form.name,
        city: form.city,
        address: form.address,
        manager_id: form.manager_id ? Number(form.manager_id) : null,
      });
      setModalOpen(false);
      setForm({ name: "", city: "", address: "", manager_id: "" });
      load();
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to create branch.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Page>
      {error ? <ErrorBox>{error}</ErrorBox> : null}

      <Head>
        <Title>Branch Management</Title>
        <Btn onClick={() => setModalOpen(true)}>+ Add Branch</Btn>
      </Head>

      <Card>
        <Toolbar>
          <Input
            placeholder="Search by branch name or city"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </Toolbar>
        <TableWrap>
          <Table>
            <thead>
              <tr>
                <th>Branch Name</th>
                <th>City</th>
                <th>Manager</th>
                <th>Employees</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: 6 }).map((_, idx) => (
                  <tr key={idx}>
                    <td colSpan={6}>
                      <Skeleton />
                    </td>
                  </tr>
                ))
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={6} style={{ color: "#64748b", textAlign: "center" }}>
                    No branches found.
                  </td>
                </tr>
              ) : (
                filtered.map((b) => (
                  <tr key={b.id}>
                    <td>{b.name}</td>
                    <td>{b.city || "-"}</td>
                    <td>{b.manager_name || "-"}</td>
                    <td>{Number(b.employee_count || 0).toLocaleString("en-IN")}</td>
                    <td>
                      <Badge $active={(b.status || "").toLowerCase() === "active"}>
                        {(b.status || "inactive").toUpperCase()}
                      </Badge>
                    </td>
                    <td>
                      <ActionLink>View</ActionLink>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </TableWrap>
      </Card>

      {modalOpen ? (
        <ModalBg onClick={() => setModalOpen(false)}>
          <Modal onClick={(e) => e.stopPropagation()}>
            <ModalHead>
              <strong style={{ color: "#0f172a" }}>Add Branch</strong>
              <ActionLink onClick={() => setModalOpen(false)} $color="#64748b">
                Close
              </ActionLink>
            </ModalHead>
            <form onSubmit={createBranch}>
              <Body>
                <Field>
                  <Label>Branch Name</Label>
                  <Input
                    required
                    value={form.name}
                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  />
                </Field>
                <Field>
                  <Label>City</Label>
                  <Input
                    value={form.city}
                    onChange={(e) => setForm((f) => ({ ...f, city: e.target.value }))}
                  />
                </Field>
                <Field style={{ gridColumn: "1 / -1" }}>
                  <Label>Address</Label>
                  <Input
                    value={form.address}
                    onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))}
                  />
                </Field>
                <Field style={{ gridColumn: "1 / -1" }}>
                  <Label>Manager</Label>
                  <Select
                    value={form.manager_id}
                    onChange={(e) => setForm((f) => ({ ...f, manager_id: e.target.value }))}
                  >
                    <option value="">Unassigned</option>
                    {users.map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.name} ({u.email})
                      </option>
                    ))}
                  </Select>
                </Field>
              </Body>
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, padding: 16, borderTop: "1px solid #e2e8f0" }}>
                <Btn type="button" $secondary onClick={() => setModalOpen(false)}>
                  Cancel
                </Btn>
                <Btn type="submit" disabled={submitting}>
                  {submitting ? "Saving..." : "Create Branch"}
                </Btn>
              </div>
            </form>
          </Modal>
        </ModalBg>
      ) : null}
    </Page>
  );
}

export default BranchManagementScreen;
